import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import {
  isStandaloneApp,
  requestPwaInstallGuide,
  subscribePwaLocationGuideRequest,
  getPwaPlatform,
  type PwaPlatform,
} from '@/lib/pwa';

/**
 * インストール案内モーダル（PwaInstallPrompt）とトーンを揃えるため、
 * 未ログインでも崩れないようブランドカラー（赤系）をローカルに固定する。
 */
const BRAND_THEME_VARS = {
  '--theme-primary': '#d92d20',
  '--theme-primary-hover': '#b42318',
  '--theme-primary-light': '#fef3f2',
} as CSSProperties;

interface LocationGuideContent {
  /** 端末名（見出し） */
  label: string;
  /** 端末アイコン（Remix Icon） */
  icon: string;
  /** 起動までの手順 */
  steps: string[];
  /** 補足（端末固有の注意） */
  note?: string;
}

/**
 * 端末ごとの「アプリはどこにある？」案内。
 * ホーム画面・アプリ一覧・スタートメニューなど、起動導線が端末で異なるため、
 * 自動判定した端末に応じた手順を表示する。
 */
const PLATFORM_GUIDES: Record<PwaPlatform, LocationGuideContent> = {
  ios: {
    label: 'iPhone / iPad',
    icon: 'ri-apple-fill',
    steps: [
      'ホーム画面（アプリのアイコンが並ぶ画面）を開きます',
      '「A1Gグループウェア」のアイコンを探してタップします',
      '見つからないときは、ホーム画面を左右にスワイプして最終ページやフォルダの中を確認してください',
    ],
    note: 'iOSは、Safariの「共有」→「ホーム画面に追加」で追加したアイコンから起動します。',
  },
  android: {
    label: 'Android',
    icon: 'ri-android-fill',
    steps: [
      'ホーム画面のアイコンをタップして起動します',
      '見つからないときは、ホーム画面を下から上にスワイプして「アプリ一覧（ドロワー）」を開きます',
      '一覧から「A1Gグループウェア」を探してタップします',
    ],
    note: '端末やランチャーの設定によっては、ホーム画面ではなくアプリ一覧に格納されます。',
  },
  windows: {
    label: 'Windows',
    icon: 'ri-windows-fill',
    steps: [
      'スタートメニューを開き、アプリ一覧から「A1Gグループウェア」を選択します',
      'または、タスクバーやデスクトップに作成されたアイコンをクリックします',
      'よく使う場合は、スタートやタスクバーにピン留めしておくと便利です',
    ],
    note: 'アドレスバー右側のインストールアイコンから追加したアプリは、スタートメニューに登録されます。',
  },
  macos: {
    label: 'Mac',
    icon: 'ri-apple-fill',
    steps: [
      'Launchpad（ローンチパッド）を開きます',
      '「A1Gグループウェア」のアイコンをクリックして起動します',
      'Dock や「アプリケーション」フォルダからも起動できます',
    ],
    note: 'Dock にドラッグして追加しておくと、次回からすぐ起動できます。',
  },
  linux: {
    label: 'Linux',
    icon: 'ri-ubuntu-fill',
    steps: [
      'デスクトップ環境のアプリケーションメニューを開きます',
      '「A1Gグループウェア」を選択して起動します',
    ],
  },
  other: {
    label: 'その他の端末',
    icon: 'ri-computer-line',
    steps: [
      'ホーム画面、またはブラウザのアプリ一覧から「A1Gグループウェア」を探して起動します',
    ],
    note: '見つからない場合は、端末の検索やアプリ一覧からアプリ名で探してみてください。',
  },
};

/**
 * 「アプリの場所」を案内するグローバルモーダル。
 *
 * - 通知設定パネル・ログイン画面の導線、またはインストール完了時に開く
 * - 端末を自動判定し、ホーム画面 / アプリ一覧 / スタートメニューなどの
 *   起動場所をクライアントごとに案内する
 * - 未インストールの端末には、インストールご案内への導線も併せて出す
 */
export default function PwaLocationGuide() {
  const [visible, setVisible] = useState(false);
  const platform = getPwaPlatform();
  const guide = PLATFORM_GUIDES[platform];

  // 各所からの表示リクエストを購読する
  useEffect(() => subscribePwaLocationGuideRequest(() => setVisible(true)), []);

  // インストール完了直後に、アプリの場所を案内する
  // （iOS Safari は appinstalled を発火しないため、その場合は手動導線のみ）
  useEffect(() => {
    const handleInstalled = () => {
      window.setTimeout(() => setVisible(true), 1200);
    };
    window.addEventListener('appinstalled', handleInstalled);
    return () => window.removeEventListener('appinstalled', handleInstalled);
  }, []);

  const close = useCallback(() => setVisible(false), []);

  const handleOpenInstallGuide = () => {
    setVisible(false);
    requestPwaInstallGuide();
  };

  return (
    <div style={BRAND_THEME_VARS}>
      <Modal
        open={visible}
        onClose={close}
        title="アプリはどこにある？"
        subtitle="インストールしたアプリの起動場所を、端末ごとに案内します"
        maxWidth="max-w-md"
        icon={
          <span className="w-10 h-10 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
            <i className="ri-apps-2-line text-xl"></i>
          </span>
        }
        footer={
          <Button variant="outline" size="sm" onClick={close}>
            閉じる
          </Button>
        }
      >
        <div className="space-y-3">
          {/* 検出した端末と起動場所の案内 */}
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
                <i className={`${guide.icon} text-lg`}></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-400">お使いの端末</p>
                <p className="text-sm font-semibold text-gray-900">{guide.label}</p>
              </div>
            </div>

            <ol className="mt-3 space-y-2">
              {guide.steps.map((step, index) => (
                <li key={step} className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0 text-[11px] font-semibold mt-px">
                    {index + 1}
                  </span>
                  <span className="text-xs leading-relaxed text-gray-700">{step}</span>
                </li>
              ))}
            </ol>

            {guide.note && (
              <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-gray-500">
                <i className="ri-information-line mt-px shrink-0 text-gray-400"></i>
                <span className="min-w-0 flex-1">{guide.note}</span>
              </p>
            )}
          </div>

          {/* 見つからないとき / 未インストールへの導線 */}
          <div className="rounded-xl border border-gray-200 p-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)] flex items-center justify-center shrink-0">
              <i className="ri-question-line text-lg"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">アプリが見つからないとき</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                まだこの端末にインストールされていない可能性があります。インストールすると、
                ホーム画面やスタートメニューからアプリのように起動できます。
              </p>
              {!isStandaloneApp() && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    icon="ri-smartphone-line"
                    onClick={handleOpenInstallGuide}
                  >
                    インストールのご案内を見る
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}